import { type BlockedMember, profileApi, userApi } from '@/features/profile-management';
import { getStorageUrl } from '@/shared/lib';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { Ban, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Settings card listing the members blocked by the current user, each with
 * an "unblock" action: they cannot be reached anywhere else in the app.
 */
export function BlockedMembersCard() {
  const { t } = useTranslate();
  const queryClient = useQueryClient();

  const { data: members, isLoading } = useQuery({
    queryKey: ['blockedMembers'],
    queryFn: userApi.getBlockedMembers,
  });

  const unblockMutation = useMutation({
    mutationFn: (member: BlockedMember) => profileApi.unblockMember(member.nodeId),
    onSuccess: (_, member) => {
      toast.success(t('block.unblocked', { username: member.username }));
      // The member shows up again in the lists
      for (const queryKey of [
        ['blockedMembers'],
        ['profiles'],
        ['converses'],
        ['interactionProfiles'],
        ['matches'],
      ]) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  return (
    <Card data-testid="blocked-members">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center">
            <Ban className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <CardTitle className="font-display">{t('block.listTitle')}</CardTitle>
            <CardDescription>{t('block.listDescription')}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : members && members.length > 0 ? (
          <ul className="divide-y divide-border">
            {members.map((member) => {
              const isUnblocking =
                unblockMutation.isPending && unblockMutation.variables?.nodeId === member.nodeId;
              return (
                <li
                  key={member.nodeId}
                  className="flex items-center justify-between gap-3 py-3"
                  data-testid="blocked-member"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={getStorageUrl(member.avatarPath)} alt={member.username} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {member.username.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium truncate">{member.username}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => unblockMutation.mutate(member)}
                    disabled={unblockMutation.isPending}
                  >
                    {isUnblocking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('block.unblock')}
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t('block.empty')}</p>
        )}
      </CardContent>
    </Card>
  );
}
